import { useAuth } from "@/contexts/AuthContext";
import { useLanguage } from "@/contexts/LanguageContext";
import {
  getGreeting,
  formatFullName,
  getUserEmail,
  getUserRole,
  getProfilePictureUrl,
} from "@/utils/userHelpers";

/**
 * Custom hook that provides easy access to user information and formatted data
 */
export const useUserInfo = () => {
  const { user, isAuthenticated, loading } = useAuth();
  const { language } = useLanguage();

  // Basic user information
  const userInfo = {
    // Authentication status
    isAuthenticated,
    loading,

    // User profile
    profile: user,

    // Formatted information
    fullName: user ? formatFullName(user) : "",
    email: user ? getUserEmail(user) : "",
    role: user ? getUserRole(user) : "",
    initials: user?.initials || "",

    // Greeting
    greeting: user ? getGreeting(user.displayName, language) : "",

    // Profile picture
    profilePictureUrl: user ? getProfilePictureUrl(user) : "",

    // Additional fields
    jobTitle: user?.jobTitle || "",
    department: user?.department || "",
    companyName: user?.companyName || "",
    officeLocation: user?.officeLocation || "",
    mobilePhone: user?.mobilePhone || "",
    businessPhones: user?.businessPhones || [],

    // Microsoft specific
    tenantId: user?.tenantId || "",
    username: user?.username || "",
  };

  // Helper methods
  const hasRole = (role: string): boolean => {
    return userInfo.role.toLowerCase().includes(role.toLowerCase());
  };

  const isInDepartment = (department: string): boolean => {
    return userInfo.department.toLowerCase().includes(department.toLowerCase());
  };

  const hasPermission = (permission: string): boolean => {
    // This can be expanded based on your permission system
    return isAuthenticated;
  };

  return {
    ...userInfo,
    hasRole,
    isInDepartment,
    hasPermission,
  };
};
